import { useContext } from 'react';
import useMsal from '../../auth/useMsal';
import getContext from '../../context/AppSettingsContext';
import { SWApiMessageProps } from './SWApiMessageProps';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useConnectionStore } from 'src/context/ConnectionContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import { useRotationResponseHandler } from '../responseHandlers/useRotationResponseHandler';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';

export const useSWService = () => {
    const appSettings = useContext(getContext());
    const { interceptSWRequest } = useMsal();
    const { hasAuthorizedAppToken, getAppAccessToken } = queryParamsUtils();
    const { isOfflineSimulated } = useConnectionStore();
    const { apiEndpoints } = useApiEndpoints();
    const { onGetImosRotation } = useRotationResponseHandler();

    const setupServiceWorker = () => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((registration) => {
                setTimeout(() => removeOldCacheItems(registration), 25);
            });
            navigator.serviceWorker.addEventListener('message', (event) => handleSWResponse(event));
        }
    };

    const refreshRequestsContaining = async (endpoints: string[], queryParams?: string, additionalData?: any) => {
        if ('serviceWorker' in navigator && navigator.onLine && !(await isOfflineSimulated())) {
            const registration = await navigator.serviceWorker.ready;

            // Process each endpoint with throttling
            for (const endpoint of endpoints) {
                // Create a request-specific key for this refresh operation
                const requestKey = `refreshRequestsContaining:${endpoint}:${queryParams || ''}`;

                // Use our request throttle manager to deduplicate refresh requests
                if (!requestThrottleManager.isRequestInProgress(requestKey)) {
                    await requestThrottleManager.execute(requestKey, async () => {
                        const props: SWApiMessageProps = {
                            type: 'refresh_containing',
                            forceRefresh: true,
                            queryParams: queryParams,
                            additionalData,
                        };

                        await postApiMessage(registration, endpoint, props);
                        return true;
                    });
                } else {
                    console.log(`Skipping duplicate refresh of endpoint: ${endpoint}`);
                }
            }
        }
    };

    const removeRequestsContaining = async (endpoints: string[], queryParams?: string) => {
        if ('serviceWorker' in navigator && navigator.onLine && !(await isOfflineSimulated())) {
            const registration = await navigator.serviceWorker.ready;

            endpoints.forEach((endpoint) => {
                const props: SWApiMessageProps = {
                    type: 'remove_containing',
                    queryParams: queryParams,
                };

                postApiMessage(registration, endpoint, props);
            });
        }
    };

    const addAppAccessToken = (queryParams: string, appAccessToken: string): string => {
        return queryParams + '&appAccessToken=' + appAccessToken;
    };

    const postApiMessage = async (registration: ServiceWorkerRegistration, endpoint: string, props: SWApiMessageProps) => {
        const body = props.postBody ? JSON.stringify(props.postBody) : undefined;

        if (hasAuthorizedAppToken()) {
            const appAccessToken = getAppAccessToken();
            const queryParams = props.queryParams ? addAppAccessToken(props.queryParams, appAccessToken) : undefined;
            const queryParamsList = props.queryParamsList ? props.queryParamsList.map((o) => addAppAccessToken(o, appAccessToken)) : undefined;
            registration.active.postMessage({
                type: props.type,
                baseUrl: appSettings.api,
                endpoint,
                forceRefresh: props.forceRefresh,
                queryParams: queryParams,
                queryParamsList: queryParamsList,
                additionalData: props.additionalData,
                body,
            });
        } else {
            const accessToken = await interceptSWRequest();
            if (accessToken) {
                registration.active.postMessage({
                    type: props.type,
                    baseUrl: appSettings.api,
                    endpoint,
                    forceRefresh: props.forceRefresh,
                    queryParams: props.queryParams,
                    queryParamsList: props.queryParamsList,
                    additionalData: props.additionalData,
                    body,
                    token: accessToken,
                });
            }
        }
    };

    const callApiEndpoint = async (endpoint: string, queryParams?: string) => {
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;

            queryParams += '&releaseId=' + appSettings.releaseId;
            const props: SWApiMessageProps = {
                type: 'api_call',
                forceRefresh: false,
                queryParams: queryParams,
            };

            await postApiMessage(registration, endpoint, props);
        }
    };

    const clearCache = (): Promise<void> => {
        return new Promise((resolve) => {
            if ('serviceWorker' in navigator) {
                const handleClearCacheCompleted = (event: MessageEvent) => {
                    if (event.data?.type === 'clear_cache_completed') {
                        navigator.serviceWorker.removeEventListener('message', handleClearCacheCompleted);
                        resolve();
                    }
                };

                navigator.serviceWorker.addEventListener('message', handleClearCacheCompleted);
                navigator.serviceWorker.ready.then((registration) => {
                    registration.active.postMessage({
                        type: 'clear_cache',
                    });
                });
            } else {
                resolve();
            }
        });
    };

    const removeOldCacheItems = (registration: ServiceWorkerRegistration) => {
        const releaseId = appSettings.releaseId;
        registration.active.postMessage({
            type: 'remove_old_items',
            releaseId,
        });
    };

    const handleSWResponse = (event: MessageEvent) => {
        const responses = event.data?.responses;
        if (!responses) return;

        const arr = Array.isArray(responses);
        if (arr && responses.length > 0) {
            responses.forEach((response) => {
                if (response.url && response.responseData) {
                    handleRefreshUrl(response.url, response.responseData, event.data?.additionalData);
                }
            });
        } else {
            if (responses.url && responses.responseData) {
                handleRefreshUrl(responses.url, responses.responseData, event.data?.additionalData);
            }
        }
    };

    const handleRefreshUrl = (url: string, responseData: any, additionalData: any) => {
        const pathName = new URL(url).pathname;
        const endpoint = pathName.substring(pathName.lastIndexOf('/') + 1);

        console.log('handleRefreshUrl', endpoint);

        if (endpoint === apiEndpoints['getRotation'].url) {
            onGetImosRotation(responseData, additionalData, true);
        }
    };

    return {
        setupServiceWorker,
        callApiEndpoint,
        clearCache,
        refreshRequestsContaining,
        removeRequestsContaining,
    };
};
